import { useState } from "react";
import { useCrm, collabById } from "@/lib/crm";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Sparkles, Plus, Trash2, Euro, Users2, CalendarDays, MapPin, Handshake } from "lucide-react";
import { eur, dateIt, isAcq } from "@/lib/constants";
import { ExperienceDialog } from "@/components/ExperienceDialog";
import { useInvitiSync } from "@/lib/inviti";

export default function Progetti() {
  const { progetti, ssd, settings, collaboratori, api, refresh } = useCrm();
  const { isTitolare } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  useInvitiSync(api, refresh, isTitolare);

  const invite = async (form) => {
    await save(form, true);
    try {
      const r = await api.inviaInvitoExperience(editing?.id || form.id);
      await refresh();
      toast.success(`Invito inviato a ${r.inviati} persone${r.senza_email ? ` · ${r.senza_email} iscritti senza email` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    }
  };

  const save = async (form, keepOpen = false) => {
    if (!form.nome.trim()) { toast.error("Il nome dell'experience è obbligatorio"); return; }
    setSaving(true);
    try {
      const { id, settore, created_at, ...payload } = form; // eslint-disable-line no-unused-vars
      payload.nome = payload.nome.trim();
      payload.posti = Number(payload.posti) || 0;
      payload.prezzo = Number(payload.prezzo) || 0;
      payload.durata_min = Number(payload.durata_min) || 240;
      payload.sponsor = (payload.sponsor || []).map((x) => ({ ...x, contributo: Number(x.contributo) || 0 }));
      if (payload.costi) payload.costi = payload.costi.map((x) => ({ ...x, importo: Number(x.importo) || 0 }));
      if (editing?.id) await api.updateProgetto(editing.id, payload);
      else await api.createProgetto(payload);
      await refresh();
      if (!keepOpen) {
        setOpen(false);
        toast.success(editing?.id ? "Experience aggiornata" : "Experience creata");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare "${p.nome}"? I clienti collegati resteranno in pipeline.`)) return;
    await api.deleteProgetto(p.id);
    await refresh();
    toast.success("Experience eliminata");
  };

  return (
    <div className="space-y-6" data-testid="progetti-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-primary" /> Portrait Experience
          </h1>
          <p className="text-slate-500 mt-1">Portrait day in luoghi particolari: programma, posti, partner del progetto e partecipanti.</p>
        </div>
        {isTitolare && (
          <Button data-testid="add-progetto-button" onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full gap-1.5">
            <Plus className="h-4 w-4" /> Nuova experience
          </Button>
        )}
      </div>

      {progetti.length === 0 ? (
        <Card className="p-12 text-center text-sm text-slate-400" data-testid="progetti-empty">
          Nessuna experience. {isTitolare ? "Crea la prima (es. \"Portrait Day al Castello\") con data, luogo, posti e prezzo." : "Il titolare non ha ancora creato experience."}
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {progetti.map((p) => {
            const part = ssd.filter((x) => (x.progetto_id || "") === p.id);
            const venduti = part.filter((x) => isAcq(x, settings)).length;
            const resp = collabById(collaboratori, p.collaboratore_id);
            const rsvp = Object.values(p.rsvp || {}).reduce((a, r) => ({ ...a, [r.email]: r.risposta }), {});
            const invitati = part.filter((x) => (x.email || "").trim());
            const conta = (k) => invitati.filter((x) => rsvp[x.email.trim().toLowerCase()] === k).length;
            const conf = { si: conta("accettato"), no: conta("rifiutato"), forse: conta("forse") };
            const attesa = invitati.length - conf.si - conf.no - conf.forse;
            return (
              <Card key={p.id} data-testid={`progetto-card-${p.id}`} onClick={() => { setEditing(p); setOpen(true); }} className="p-5 animate-fade-up cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow]">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="h-3.5 w-3.5 rounded-full flex-shrink-0" style={{ background: p.colore }} />
                    <h3 className="font-semibold text-slate-900 dark:text-white truncate">{p.nome}</h3>
                  </div>
                  {isTitolare && <button data-testid={`progetto-del-${p.id}`} onClick={(e) => { e.stopPropagation(); remove(p); }} className="text-slate-300 hover:text-destructive p-1"><Trash2 className="h-4 w-4" /></button>}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 mt-2">
                  {p.data && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" />{dateIt(p.data)}</span>}
                  {p.luogo && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{p.luogo}</span>}
                  {(p.sponsor || []).length > 0 && <span className="inline-flex items-center gap-1"><Handshake className="h-3 w-3" />{p.sponsor.length} partner</span>}
                </div>
                {p.tipo && <div className="text-xs text-slate-400 mt-1">{p.tipo}</div>}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="inline-flex items-center gap-1 text-slate-500"><Users2 className="h-3.5 w-3.5" /> Posti venduti</span>
                    <span data-testid={`progetto-posti-${p.id}`} className="tabular-nums font-semibold text-slate-700 dark:text-slate-200">{venduti}/{p.posti || 0} <span className="text-slate-400 font-normal">({part.length} iscritti)</span></span>
                  </div>
                  <Progress value={p.posti ? Math.min(100, (venduti / p.posti) * 100) : 0} className="h-2" />
                </div>
                {p.invito_inviato_at && invitati.length > 0 && (
                  <div data-testid={`progetto-conferme-${p.id}`} className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px]">
                    <span className="text-slate-400 mr-1">Conferme</span>
                    <span className="px-2 py-0.5 rounded-full font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">{conf.si} sì</span>
                    {conf.forse > 0 && <span className="px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">{conf.forse} forse</span>}
                    {conf.no > 0 && <span className="px-2 py-0.5 rounded-full font-semibold bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">{conf.no} no</span>}
                    <span className="px-2 py-0.5 rounded-full font-semibold bg-slate-100 text-slate-500 dark:bg-slate-800">{attesa} in attesa</span>
                  </div>
                )}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                  <span className="inline-flex items-center gap-1 text-lg font-bold text-sky-600 tabular-nums"><Euro className="h-4 w-4" />{eur(p.prezzo)}<span className="text-xs font-normal text-slate-400">/persona</span></span>
                  <span className="text-xs text-slate-500">{resp ? resp.nome : "Nessun responsabile"}</span>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <ExperienceDialog open={open} onOpenChange={setOpen} initial={editing ? progetti.find((x) => x.id === editing.id) || editing : null} onSave={(f) => save(f)} onInvite={invite} saving={saving} isTitolare={isTitolare}
        collaboratori={collaboratori} tipi={settings?.tipi_progetto} partecipanti={editing ? ssd.filter((x) => x.progetto_id === editing.id) : []} />
    </div>
  );
}
